import { createSignal, onMount } from "solid-js";

export type ChangeCategory = "new" | "improved" | "fixed";

export interface ChangelogEntry {
  text: string;
  category: ChangeCategory;
}

export interface ChangelogVersion {
  version: string;
  date: string;
  entries: ChangelogEntry[];
}

export interface UseChangelogResult {
  hasUnread: () => boolean;
  versions: () => ChangelogVersion[];
  dismiss: () => void;
}

const STORAGE_KEY = "flowicity_changelog_last_seen";

/** Compare two semver strings. Returns positive if a > b, negative if a < b, 0 if equal. */
function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na !== nb) return na - nb;
  }
  return 0;
}

export function useChangelog(): UseChangelogResult {
  const [hasUnread, setHasUnread] = createSignal(false);
  const [versions, setVersions] = createSignal<ChangelogVersion[]>([]);

  onMount(async () => {
    let data: ChangelogVersion[];
    try {
      const resp = await fetch("/changelog.json");
      if (!resp.ok) return;
      data = await resp.json();
    } catch {
      // Fetch failed — silently skip, will retry next load
      return;
    }

    const valid = data.filter((v) => v.entries.length > 0);
    if (valid.length === 0) return;

    // Always populate versions so the modal has content when opened
    setVersions(valid);

    const lastSeen = localStorage.getItem(STORAGE_KEY);

    // First visit ever — record current version, no dot
    if (lastSeen === null) {
      localStorage.setItem(STORAGE_KEY, valid[0].version);
      return;
    }

    // Check if there are versions newer than what the user last saw
    const hasNewer = valid.some(
      (v) => compareVersions(v.version, lastSeen) > 0,
    );

    if (hasNewer) {
      setHasUnread(true);
    }
  });

  const dismiss = () => {
    const v = versions();
    if (v.length > 0) {
      localStorage.setItem(STORAGE_KEY, v[0].version);
    }
    setHasUnread(false);
  };

  return { hasUnread, versions, dismiss };
}
